import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { computeZoomDelta } from './computeZoomDelta';

const ZOOM_IN_MULTIPLIER = 1.0;
const ZOOM_OUT_MULTIPLIER = 1.5;
const DEBOUNCE_TIME = 80;
const MAX_WAIT = 200;
const WHEEL_PX_PER_ZOOM_LEVEL = 60;

export function AsymmetricZoomHandler() {
  const map = useMap();
  const deltaRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef(0);

  useEffect(() => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__TEST_MAP = map;
    }
    const container = map.getContainer();

    let lastWheelEvent: WheelEvent | null = null;

    function performZoom(mouseEvent: WheelEvent) {
      const delta = deltaRef.current;
      deltaRef.current = 0;

      if (delta === 0) return;

      const { zoomLevels, direction } = computeZoomDelta(
        delta,
        WHEEL_PX_PER_ZOOM_LEVEL,
        ZOOM_IN_MULTIPLIER,
        ZOOM_OUT_MULTIPLIER,
      );

      if (direction === 'none') return;

      const sign = direction === 'in' ? 1 : -1;
      const currentZoom = map.getZoom();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const targetZoom = (map as any)._limitZoom(currentZoom + sign * zoomLevels);

      if (targetZoom === currentZoom) return;

      const containerPoint = map.mouseEventToContainerPoint(mouseEvent);
      const latlng = map.containerPointToLatLng(containerPoint);
      map.setZoomAround(latlng, targetZoom);
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      e.stopPropagation();

      // Normalize delta: DOM_DELTA_PIXEL uses raw value, DOM_DELTA_LINE scales by 60
      // Negate so scroll-up (negative deltaY) becomes positive (zoom in)
      const delta =
        e.deltaMode === WheelEvent.DOM_DELTA_PIXEL
          ? -e.deltaY
          : -e.deltaY * 60;

      deltaRef.current += delta;
      lastWheelEvent = e;

      // maxWait debounce: trailing debounce resets on each event, but forces
      // a fire after MAX_WAIT ms from the first event in the gesture.
      // This batches trackpad swipe micro-gestures into 1-2 fires instead of 5-7.
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }

      const elapsed = Date.now() - startTimeRef.current;
      const maxWaitLeft = MAX_WAIT - elapsed;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      const delay = maxWaitLeft <= 0 ? 0 : Math.min(DEBOUNCE_TIME, maxWaitLeft);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        startTimeRef.current = 0;
        if (lastWheelEvent) {
          performZoom(lastWheelEvent);
          lastWheelEvent = null;
        }
      }, delay);
    }

    container.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', onWheel);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [map]);

  return null;
}
